import { useEffect, useEffectEvent, useState } from 'react';
import { Link } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { AlertCircleIcon, CheckIcon, CloseIcon, UploadIcon } from '@/components/icons';
import { DetailSection } from '@/components/ui/DetailSection';
import { ActionIcon } from '@/components/ui/ActionIcon';
import { buttonClasses } from '@/components/ui/button-styles';
import type { CvImport } from '../types';

/** One bounded toast for a batch, never a stack covering the page. */
export const CvImportToast = ({
  items,
  unread,
  onDismiss,
}: {
  items: CvImport[];
  unread: number;
  onDismiss: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [closing, setClosing] = useState(false);
  const paused = hovered || focused;
  const dismiss = useEffectEvent(onDismiss);
  useEffect(() => {
    if (paused) return;
    let end: ReturnType<typeof setTimeout> | undefined;
    const timeout = setTimeout(() => {
      setClosing(true);
      end = setTimeout(() => dismiss(), 200);
    }, 3000);
    return () => {
      clearTimeout(timeout);
      if (end) clearTimeout(end);
    };
  }, [paused]);
  const success = items.filter((item) => item.status === 'COMPLETED').length;
  const failures = items.filter((item) => item.status === 'FAILED').length;
  const incomplete = items.filter((item) => item.status === 'NEEDS_CONTACT').length;
  return (
    <aside
      className="cv-import-notice"
      data-closing={closing && !paused}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <DetailSection
        title="Imports de CV"
        icon={<UploadIcon />}
        className="cv-import-notice__card"
        action={
          <ActionIcon
            label="Masquer la notification"
            className="size-8 rounded-lg text-base"
            onClick={onDismiss}
          >
            <CloseIcon />
          </ActionIcon>
        }
      >
        <h3 className="text-navy-900 text-sm font-semibold">
          {items.length === 1
            ? incomplete
              ? 'Un CV attend ses coordonnées'
              : failures
                ? 'Un CV n’a pas pu être importé'
                : 'Un CV a été intégré'
            : items.length + ' imports terminés'}
        </h3>
        <p className="text-ink-500 mt-1 text-xs leading-5">
          {[
            success ? success + ' CV intégré' + (success > 1 ? 's' : '') : '',
            failures ? failures + ' en échec' : '',
            incomplete ? incomplete + ' à compléter' : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <ul className="cv-import-notice__files">
          {items.slice(0, 3).map((item) => (
            <li key={item.id}>
              <span
                className={item.status !== 'COMPLETED' ? 'text-red-600' : 'text-emerald-600'}
                aria-hidden
              >
                {item.status !== 'COMPLETED' ? <AlertCircleIcon /> : <CheckIcon />}
              </span>
              <div className="min-w-0">
                <p
                  className="truncate text-xs font-medium text-navy-900"
                  title={item.originalFilename}
                >
                  {item.originalFilename}
                </p>
                {item.candidate && (
                  <p className="text-ink-500 mt-0.5 truncate text-xs">
                    {item.candidate.displayName}
                  </p>
                )}
                {item.status === 'NEEDS_CONTACT' && (
                  <Link
                    to={ROUTES.cvContactCompletion(item.id)}
                    onClick={onDismiss}
                    className="mt-1 block text-xs text-accent-700 hover:underline"
                  >
                    Compléter les coordonnées →
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
        {items.length > 3 && (
          <p className="text-ink-500 mt-2 text-xs">
            Et {items.length - 3} autre(s) résultat(s) dans le suivi.
          </p>
        )}
        <div className="border-navy-100 mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <span className="text-ink-500 text-xs tabular-nums">
            {unread} résultat{unread > 1 ? 's' : ''} non lu{unread > 1 ? 's' : ''}
          </span>
          <Link
            className={buttonClasses({ variant: 'secondary', size: 'sm' })}
            to={ROUTES.candidateImport}
            onClick={onDismiss}
          >
            Consulter les résultats →
          </Link>
        </div>
      </DetailSection>
    </aside>
  );
};
